import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Wallet, TrendingUp, CalendarClock, FileText, ArrowDownCircle, ArrowUpCircle, Landmark, Banknote, HandCoins, Copy, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { useAuth } from "@/context/AuthContext";
import { ChartsBlock } from "@/components/InvestmentsAdminTab";
import puntoPlayLogo from "@/assets/puntoplay-logo.png";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null ? "—" : `${Number(n)}%`);

const TYPE_META = {
  APORTE_CAPITAL: { label: "Aporte de capital", color: "text-emerald-600", Icon: ArrowUpCircle },
  RETIRO_CAPITAL: { label: "Retiro de capital", color: "text-rose-600", Icon: ArrowDownCircle },
  INTERES_GENERADO: { label: "Interés generado", color: "text-blue-600", Icon: TrendingUp },
  PAGO_INTERES: { label: "Pago de interés", color: "text-violet-600", Icon: Banknote },
};

function PortalLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false); const [showPwd, setShowPwd] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email.trim().toLowerCase(), password); } catch { toast.error("Credenciales inválidas"); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#0b0f17] px-4" data-testid="investor-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl p-7 text-white">
        <div className="flex flex-col items-center mb-6">
          <div className="rounded-2xl bg-white px-5 py-3.5 mb-4 flex items-center justify-center"><img src={puntoPlayLogo} alt="Punto Play" className="h-9 w-auto" data-testid="investor-login-logo" /></div>
          <h1 className="text-xl font-semibold tracking-tight">Portal Inversionistas</h1>
          <p className="text-sm text-white/50 mt-1">Acceso privado</p>
        </div>
        <label className="text-xs text-white/60">Correo</label>
        <Input data-testid="investor-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl mt-1 mb-3 bg-white/10 border-white/10 text-white placeholder:text-white/30" placeholder="tu@correo.com" />
        <label className="text-xs text-white/60">Contraseña</label>
        <div className="relative mt-1 mb-5">
          <Input data-testid="investor-login-password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl pr-11 bg-white/10 border-white/10 text-white" />
          <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="investor-login-password-eye" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        </div>
        <Button data-testid="investor-login-submit" disabled={loading} className="h-11 w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
        </Button>
      </form>
    </div>
  );
}

const Stat = ({ label, value, accent, testid }) => (
  <div className={`rounded-2xl border p-3 sm:p-4 min-w-0 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-zinc-200"}`} data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-zinc-400 truncate">{label}</p>
    <p className={`text-base sm:text-xl font-bold mt-1 tracking-tight tabular-nums leading-tight break-all ${accent ? "text-emerald-700" : "text-zinc-900"}`}>{value}</p>
  </div>
);

const openReceipt = async (id) => {
  const w = window.open("", "_blank");
  try {
    const r = await adminApi.get(`/investor/ledger/${id}/receipt`);
    const data = r.data.receipt;
    if (!data) { if (w) w.close(); toast.error("Sin comprobante"); return; }
    const isData = typeof data === "string" && data.startsWith("data:");
    const url = isData ? URL.createObjectURL(await (await fetch(data)).blob()) : data;
    if (w) w.location.href = url; else window.open(url, "_blank");
    if (isData) setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) w.close(); toast.error("Sin comprobante"); }
};

function YieldsByPeriod({ ledger }) {
  const gen = (ledger || []).filter((e) => e.type === "INTERES_GENERADO");
  const byPeriod = {};
  gen.forEach((e) => { const p = e.period || (e.effective_date || "").slice(0, 7); (byPeriod[p] = byPeriod[p] || []).push(e); });
  const periods = Object.keys(byPeriod).sort().reverse();
  if (periods.length === 0) return null;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="investor-yields-by-period">
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Rendimientos por período</p>
      <div className="divide-y divide-zinc-100">
        {periods.map((p) => {
          const items = byPeriod[p];
          const total = items.reduce((s, e) => s + Number(e.amount), 0);
          return (
            <div key={p} className="py-2.5" data-testid={`investor-yield-period-${p}`}>
              <div className="flex items-center justify-between"><p className="text-sm font-semibold">{p}</p><p className="text-sm font-semibold text-blue-600">{money(total)}</p></div>
              {items.map((e) => (
                <div key={e.id} className="mt-1 flex items-center justify-between text-[11px] text-zinc-500">
                  <span className="truncate">
                    {e.capital_base != null ? `capital ${money(e.capital_base)}` : "capital —"}
                    {e.rate_pct != null ? ` · ${e.rate_pct}%` : ""}
                    {e.prorated && e.days != null ? ` · ${e.days} días` : ""}
                    {e.note ? ` · ${e.note}` : ""}
                  </span>
                  <span className="font-medium text-zinc-700 shrink-0 ml-2">{money(e.amount)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Bloque para el inversionista: muestra el MONTO REAL de interés generado por mes (nunca tasas).
function AnnualYield({ ledger, initialCalendar }) {
  const byPeriod = {};
  (ledger || []).filter((e) => e.type === "INTERES_GENERADO").forEach((e) => {
    const p = e.period || (e.effective_date || "").slice(0, 7);
    if (p) byPeriod[p] = (byPeriod[p] || 0) + Number(e.amount || 0);
  });
  const nowY = String(new Date().getFullYear());
  const years = [...new Set(Object.keys(byPeriod).map((p) => p.slice(0, 4)))];
  if (initialCalendar?.year && !years.includes(initialCalendar.year)) years.push(initialCalendar.year);
  if (!years.length) years.push(nowY);
  years.sort().reverse();
  const [year, setYear] = useState(years.includes(nowY) ? nowY : years[0]);
  const [calCache, setCalCache] = useState(initialCalendar ? { [initialCalendar.year]: initialCalendar } : {});

  useEffect(() => {
    if (!calCache[year]) adminApi.get(`/investor/calendar?year=${year}`).then((r) => setCalCache((c) => ({ ...c, [year]: r.data }))).catch(() => {});
    // eslint-disable-next-line
  }, [year]);

  const graceMonth = calCache[year]?.grace_month;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="investor-annual-yield">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Rendimiento anual · {year}</p>
        {years.length > 1 && (
          <div className="flex gap-1 shrink-0" data-testid="investor-annual-year-selector">
            {years.map((y) => (
              <button key={y} data-testid={`investor-annual-year-${y}`} onClick={() => setYear(y)} className={`h-7 px-2.5 rounded-lg text-xs font-semibold transition-colors ${year === y ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}>{y}</button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {MONTH_NAMES.map((name, idx) => {
          const m = idx + 1;
          const amt = byPeriod[`${year}-${String(m).padStart(2, "0")}`];
          const isGrace = graceMonth === m;
          return (
            <div key={m} className={`rounded-xl border px-3 py-2 ${isGrace ? "bg-amber-50 border-amber-200" : amt != null ? "bg-emerald-50/60 border-emerald-100" : "bg-white border-zinc-200"}`} data-testid={`investor-annual-${m}`}>
              <p className="text-[11px] text-zinc-500">{name}</p>
              <p className={`text-sm font-semibold ${isGrace ? "text-amber-700" : amt != null ? "text-emerald-700" : "text-zinc-400"}`}>
                {isGrace ? "$0 · Mes de gracia" : amt != null ? money(amt) : "Pendiente"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
const SHORT_DATE = (s) => (s ? new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" }).replace(".", "") : "—");
const TINT = {
  APORTE_CAPITAL: { text: "text-emerald-700", chip: "bg-emerald-50 text-emerald-600" },
  RETIRO_CAPITAL: { text: "text-rose-700", chip: "bg-rose-50 text-rose-600" },
  INTERES_GENERADO: { text: "text-blue-700", chip: "bg-blue-50 text-blue-600" },
  PAGO_INTERES: { text: "text-violet-700", chip: "bg-violet-50 text-violet-600" },
};
const FILTERS = {
  all: { label: "Todos", fn: () => true },
  rend: { label: "Rendimientos", fn: (e) => e.type === "INTERES_GENERADO" },
  pagos: { label: "Pagos", fn: (e) => e.type === "PAGO_INTERES" },
  capital: { label: "Capital", fn: (e) => e.type === "APORTE_CAPITAL" || e.type === "RETIRO_CAPITAL" },
};

function MovementsView({ ledger }) {
  const years = [...new Set((ledger || []).map((e) => (e.effective_date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const nowY = String(new Date().getFullYear());
  const [year, setYear] = useState(years.includes(nowY) ? nowY : years[0] || nowY);
  const [filter, setFilter] = useState("all");
  useEffect(() => { if (years.length && !years.includes(year)) setYear(years.includes(nowY) ? nowY : years[0]); /* eslint-disable-next-line */ }, [ledger]);

  if (!ledger || ledger.length === 0) return <div className="rounded-2xl border border-zinc-200 bg-white"><p className="text-sm text-zinc-400 py-10 text-center">Aún no hay movimientos.</p></div>;

  const rows = ledger.filter((e) => (e.effective_date || "").slice(0, 4) === year && FILTERS[filter].fn(e));
  const groups = {};
  rows.forEach((e) => { const mi = Number((e.effective_date || "").slice(5, 7)) - 1; (groups[mi] = groups[mi] || []).push(e); });
  const monthKeys = Object.keys(groups).map(Number).sort((a, b) => b - a);
  monthKeys.forEach((mi) => groups[mi].sort((a, b) => ((b.effective_date || "") + (b.created_at || "")).localeCompare((a.effective_date || "") + (a.created_at || ""))));

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="investor-filters">
        {Object.entries(FILTERS).map(([k, v]) => (
          <button key={k} data-testid={`investor-filter-${k}`} onClick={() => setFilter(k)} className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-medium transition-colors ${filter === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{v.label}</button>
        ))}
      </div>
      {years.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5" data-testid="investor-year-selector">
          {years.map((y) => (
            <button key={y} data-testid={`investor-year-${y}`} onClick={() => setYear(y)} className={`shrink-0 h-9 px-4 rounded-xl text-sm font-semibold transition-colors ${year === y ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>{y}</button>
          ))}
        </div>
      )}
      {monthKeys.length === 0 ? (
        <div className="rounded-2xl border border-zinc-200 bg-white"><p className="text-sm text-zinc-400 py-10 text-center">Sin movimientos para este año o filtro.</p></div>
      ) : monthKeys.map((mi) => (
        <div key={mi} data-testid={`investor-month-${year}-${mi + 1}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 px-1 mb-1.5">{MONTH_NAMES[mi]} {year}</p>
          <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100 overflow-hidden">
            {groups[mi].map((e) => {
              const M = TYPE_META[e.type] || {}; const Icon = M.Icon || Wallet; const t = TINT[e.type] || { text: "text-zinc-700", chip: "bg-zinc-100 text-zinc-500" };
              const sign = e.type === "RETIRO_CAPITAL" ? "−" : "";
              const secondary = [SHORT_DATE(e.effective_date), e.method, e.type === "INTERES_GENERADO" && e.period ? e.period : null].filter(Boolean).join(" · ");
              return (
                <div key={e.id} className="flex items-center gap-3 px-3.5 py-3" data-testid={`investor-led-${e.id}`}>
                  <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${t.chip}`}><Icon className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{M.label || e.type}</p>
                    <p className="text-xs text-zinc-600 truncate">{secondary}</p>
                    {e.note && <p className="text-[11px] text-zinc-400 truncate">{e.note}</p>}
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className={`text-sm font-bold ${t.text}`}>{sign}{money(e.amount)}</span>
                    {e.has_receipt && <button data-testid={`investor-led-receipt-${e.id}`} onClick={() => openReceipt(e.id)} className="text-emerald-700" title="Ver comprobante"><FileText className="w-4 h-4" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const WR_STATUS = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  PAGADA: { label: "Pagada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CANCELADA: { label: "Cancelada", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

function WithdrawalSection({ investorName, activeCapital, interestPending = 0, interestGenerated = 0, interestPaid = 0 }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("CAPITAL");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const isInterest = kind === "INTERES";
  const maxAvail = isInterest ? Number(interestPending || 0) : Number(activeCapital || 0);
  const openKind = (k) => { setKind(k); setCreated(null); setAmount(""); setNote(""); setOpen(true); };

  const load = () => adminApi.get("/investor/withdrawal-requests").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const reset = () => { setOpen(false); setAmount(""); setCreated(null); };
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Ingresa un monto mayor a 0"); return; }
    if (amt > maxAvail) { toast.error(isInterest ? "El monto supera tus intereses pendientes disponibles" : "El monto supera tu capital activo disponible"); return; }
    setSaving(true);
    try {
      const r = await adminApi.post("/investor/withdrawal-requests", { amount: amt, kind, note: note || null });
      setCreated(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la solicitud"); }
    finally { setSaving(false); }
  };

  const buildText = (o) => `SOLICITUD DE ${o.kind === "INTERES" ? "RETIRO DE INTERESES" : "RETIRO DE CAPITAL"}\nInversionista: ${investorName || "—"}\nMonto solicitado: ${money(o.amount)}\n${o.kind === "INTERES" ? "Intereses pendientes" : "Capital activo"} actual: ${money(o.capital_at_request)}\nFecha: ${SHORT_DATE(o.created_at)}\nOrden: ${o.id}\nEstado: Pendiente`;
  const copy = (o) => navigator.clipboard.writeText(buildText(o)).then(() => toast.success("Solicitud copiada")).catch(() => toast.error("No se pudo copiar"));

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="investor-withdrawal-section">
      <div className="space-y-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><HandCoins className="w-3.5 h-3.5" /> Retiros</p>
          <p className="text-xs text-zinc-500 mt-0.5">Solicita un retiro; tu administrador lo procesará.</p>
        </div>
        <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2">
          <Button data-testid="investor-withdraw-open" onClick={() => openKind("CAPITAL")} className="h-9 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white text-xs">Solicitar retiro de capital</Button>
          <Button data-testid="investor-withdraw-interest-open" onClick={() => openKind("INTERES")} className="h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs">Solicitar retiro de intereses</Button>
        </div>
      </div>
      {list && list.length > 0 && (
        <div className="pt-1" data-testid="investor-withdrawal-list">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">Mis solicitudes</p>
          <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
            {list.map((o) => {
              const st = WR_STATUS[o.status] || WR_STATUS.PENDIENTE;
              return (
                <div key={o.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`investor-wr-${o.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{money(o.amount)} <span className={`align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${o.kind === "INTERES" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`investor-wr-kind-${o.id}`}>{o.kind === "INTERES" ? "Intereses" : "Capital"}</span></p>
                    <p className="text-[11px] text-zinc-500 truncate">{o.id} · {SHORT_DATE(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`investor-wr-status-${o.id}`}>{st.label}</span>
                    {o.status === "PENDIENTE" && <button data-testid={`investor-wr-copy-${o.id}`} onClick={() => copy(o)} className="text-zinc-400 hover:text-zinc-700" title="Copiar solicitud"><Copy className="w-4 h-4" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && reset()}>
        <DialogContent className="max-w-sm" data-testid="investor-withdraw-dialog">
          {!created ? (
            <>
              <DialogHeader><DialogTitle>{isInterest ? "Solicitar retiro de intereses" : "Solicitar retiro de capital"}</DialogTitle><DialogDescription>Esto crea una solicitud pendiente; no modifica tus saldos hasta que el administrador la procese.</DialogDescription></DialogHeader>
              {isInterest ? (
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-2.5"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Generados</p><p className="text-sm font-bold text-zinc-900">{money(interestGenerated)}</p></div>
                  <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-2.5"><p className="text-[10px] uppercase tracking-wider text-zinc-400">Pagados</p><p className="text-sm font-bold text-zinc-900">{money(interestPaid)}</p></div>
                  <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-2.5"><p className="text-[10px] uppercase tracking-wider text-indigo-500">Pendientes</p><p className="text-sm font-bold text-indigo-700" data-testid="investor-withdraw-interest-avail">{money(interestPending)}</p></div>
                </div>
              ) : (
                <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3"><p className="text-[11px] uppercase tracking-wider text-zinc-400">Capital activo actual</p><p className="text-xl font-bold text-zinc-900" data-testid="investor-withdraw-capital">{money(activeCapital)}</p></div>
              )}
              <label className="text-xs text-zinc-500">Monto a retirar</label>
              <Input data-testid="investor-withdraw-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl" placeholder="0.00" />
              <label className="text-xs text-zinc-500">Nota (opcional)</label>
              <Input data-testid="investor-withdraw-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-11 rounded-xl" placeholder="Referencia o comentario" />
              <Button data-testid="investor-withdraw-submit" onClick={submit} disabled={saving} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar solicitud"}</Button>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle>Solicitud creada</DialogTitle><DialogDescription>Compártela con tu administrador por WhatsApp o correo.</DialogDescription></DialogHeader>
              <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-sm space-y-1" data-testid="investor-withdraw-created">
                <p className="font-semibold">{created.id}</p>
                <p className="text-zinc-600">Monto: <b>{money(created.amount)}</b></p>
                <p className="text-zinc-600">{created.kind === "INTERES" ? "Intereses pendientes" : "Capital activo"}: {money(created.capital_at_request)}</p>
                <p className="text-zinc-600">Estado: Pendiente</p>
              </div>
              <Button data-testid="investor-withdraw-copy" onClick={() => copy(created)} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white mt-1"><Copy className="w-4 h-4 mr-1.5" /> Copiar solicitud</Button>
              <Button data-testid="investor-withdraw-close" variant="outline" onClick={reset} className="h-10 rounded-xl">Cerrar</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function InvestorPortal() {
  const { admin, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [view, setView] = useState("inicio");

  useEffect(() => {
    if (admin && admin.role === "inversionista") {
      adminApi.get("/investor/me").then((r) => setMe(r.data)).catch(() => {});
      adminApi.get("/investor/dashboard").then((r) => setDash(r.data)).catch(() => {});
      adminApi.get("/investor/ledger").then((r) => setLedger(r.data)).catch(() => {});
      adminApi.get("/investor/calendar").then((r) => setCalendar(r.data)).catch(() => {});
    }
  }, [admin]);

  if (admin === null) return <div className="min-h-screen grid place-items-center text-zinc-400">Cargando…</div>;
  if (!admin) return <PortalLogin />;
  if (admin.role !== "inversionista") {
    return <div className="min-h-screen grid place-items-center text-center px-6"><div><p className="text-zinc-700 font-medium">Esta cuenta no es de inversionista.</p><a href="/admin" className="text-sm text-emerald-700 underline">Ir al panel</a></div></div>;
  }

  const b = dash?.balances || {};
  const ny = dash?.next_yield;
  const NAV = [["inicio", "Inicio"], ["evolucion", "Evolución"], ["historial", "Movimientos"]];

  return (
    <div className="min-h-screen bg-[#f7f7f8] text-zinc-900">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-xl bg-emerald-500 grid place-items-center text-white"><Landmark className="w-4 h-4" /></div><span className="font-semibold">Inversionistas</span></div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-500 hidden sm:inline" data-testid="investor-name">{me?.investor_name}</span>
            <Button variant="outline" size="sm" data-testid="investor-logout" onClick={logout} className="h-9 rounded-lg"><LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Salir</span></Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 flex gap-1 -mb-px overflow-x-auto">
          {NAV.map(([k, label]) => (
            <button key={k} data-testid={`investor-nav-${k}`} onClick={() => setView(k)} className={`px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${view === k ? "border-emerald-600 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>{label}</button>
          ))}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">
        {view === "inicio" && (
          <div className="space-y-6" data-testid="investor-inicio">
            <div><h1 className="text-2xl font-semibold tracking-tight">Hola, {me?.investor_name || "inversionista"}</h1><p className="text-sm text-zinc-500 mt-1">Resumen de tu capital y rendimientos</p></div>
            <div className="rounded-3xl bg-zinc-900 text-white p-6">
              <p className="text-[12px] uppercase tracking-wider text-white/50 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Capital activo</p>
              <p className="text-4xl font-bold mt-1.5 tracking-tight" data-testid="investor-active-capital">{money(b.active_capital)}</p>
              <p className="text-[12px] text-white/60 mt-2">Aportado {money(b.total_contributed)} · retirado {money(b.total_withdrawn)}</p>
            </div>
            <div className="grid grid-cols-2 min-[360px]:grid-cols-3 gap-2 sm:gap-3">
              <Stat label="Generados" value={money(b.interest_generated)} testid="investor-generated" />
              <Stat label="Pagados" value={money(b.interest_paid)} testid="investor-paid" />
              <Stat label="Pendientes" value={money(b.interest_pending)} accent testid="investor-pending" />
            </div>
            <WithdrawalSection investorName={me?.investor_name} activeCapital={b.active_capital} interestPending={b.interest_pending} interestGenerated={b.interest_generated} interestPaid={b.interest_paid} />
            <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="investor-next">
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Próximo rendimiento</p>
              {ny?.rate_pct != null ? (
                <div className="mt-1">
                  <p className="font-semibold text-lg">{money(ny.projected_interest)}</p>
                  <p className="text-xs text-zinc-500">Período {ny.period}{ny.grace_zero ? ` · mes de gracia ${ny.candidate_period} genera 0%` : ""} · tasa {pct(ny.rate_pct)} sobre {money(ny.capital_base)}</p>
                  <p className="text-[11px] text-zinc-400 mt-1">Proyección estimada, aún no generada.</p>
                </div>
              ) : <p className="text-sm text-zinc-400 mt-1">Pendiente de definir.</p>}
            </div>
            {(ledger?.length > 0 || calendar) && (
              <AnnualYield ledger={ledger} initialCalendar={calendar} />
            )}
          </div>
        )}

        {view === "evolucion" && (
          <div className="space-y-4" data-testid="investor-evolucion">
            <h2 className="text-lg font-semibold">Evolución</h2>
            <ChartsBlock capital={dash?.capital_series} interest={dash?.interest_series} />
            <YieldsByPeriod ledger={ledger} />
          </div>
        )}

        {view === "historial" && (
          <div className="space-y-4" data-testid="investor-historial">
            <h2 className="text-lg font-semibold">Movimientos</h2>
            <MovementsView ledger={ledger} />
          </div>
        )}
      </main>
    </div>
  );
}
